import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo } from 'react';

import type { TimeSlot } from '@/features/appointments/utils/TimeSlot';
import useAppointmentStore from '@/store/AppointmentStore';

interface UseTimeSlotSelectionReturn {
  selectedDate: Date | undefined;
  selectedStartTime: string;
  selectedEndTime: string;
  handleDateChange: (date: Date) => void;
  handleTimeSlotChange: (timeSlot: TimeSlot | null) => void;
}

const findSlotForDate = (
  timeSlots: TimeSlot[],
  date: Date,
): TimeSlot | undefined => {
  if (!date || timeSlots.length === 0) return undefined;
  return timeSlots.find((slot) =>
    dayjs(slot.startTime).isSame(dayjs(date), 'day'),
  );
};

const isValidTimeSlot = (
  timeSlots: TimeSlot[],
  startTime: string,
  endTime: string,
): boolean => {
  if (!startTime || !endTime || timeSlots.length === 0) return false;
  return timeSlots.some(
    (slot) => slot.startTime === startTime && slot.endTime === endTime,
  );
};

const isDateAvailable = (
  timeSlots: TimeSlot[],
  date: Date | undefined,
): boolean => {
  if (!date || timeSlots.length === 0) return false;
  return timeSlots.some((slot) =>
    dayjs(slot.startTime).isSame(dayjs(date), 'day'),
  );
};

const getFirstSlot = (timeSlots: TimeSlot[]): TimeSlot | undefined => {
  return timeSlots.length > 0 ? timeSlots[0] : undefined;
};

const useTimeSlotSelection = (
  timeSlots: TimeSlot[],
): UseTimeSlotSelectionReturn => {
  const selectedDate = useAppointmentStore((state) => state.selectedDate);
  const selectedStartTime = useAppointmentStore(
    (state) => state.selectedStartTime,
  );
  const selectedEndTime = useAppointmentStore((state) => state.selectedEndTime);
  const setSelectedDate = useAppointmentStore((state) => state.setSelectedDate);
  const setSelectedStartTime = useAppointmentStore(
    (state) => state.setSelectedStartTime,
  );
  const setSelectedEndTime = useAppointmentStore(
    (state) => state.setSelectedEndTime,
  );

  const hasValidTimeSlots = useMemo(
    () => timeSlots.length > 0,
    [timeSlots.length],
  );
  const hasAnySelection = useMemo(
    () => selectedDate || selectedStartTime || selectedEndTime,
    [selectedDate, selectedStartTime, selectedEndTime],
  );
  const isCurrentSelectionValid = useMemo(() => {
    if (!selectedDate || !selectedStartTime || !selectedEndTime) return false;
    return (
      isDateAvailable(timeSlots, selectedDate) &&
      isValidTimeSlot(timeSlots, selectedStartTime, selectedEndTime)
    );
  }, [timeSlots, selectedDate, selectedStartTime, selectedEndTime]);

  const updateTimeSlot = useCallback(
    (timeSlot: TimeSlot | null) => {
      if (timeSlot) {
        setSelectedStartTime(timeSlot.startTime);
        setSelectedEndTime(timeSlot.endTime);
      } else {
        setSelectedStartTime('');
        setSelectedEndTime('');
      }
    },
    [setSelectedStartTime, setSelectedEndTime],
  );

  const updateDateAndTimeSlot = useCallback(
    (date: Date, timeSlot?: TimeSlot) => {
      setSelectedDate(date);
      if (timeSlot) {
        setSelectedStartTime(timeSlot.startTime);
        setSelectedEndTime(timeSlot.endTime);
      } else {
        setSelectedStartTime('');
        setSelectedEndTime('');
      }
    },
    [setSelectedDate, setSelectedStartTime, setSelectedEndTime],
  );

  useEffect(() => {
    if (!hasValidTimeSlots || hasAnySelection) return;

    const firstSlot = getFirstSlot(timeSlots);
    if (firstSlot) {
      const firstDate = new Date(firstSlot.startTime);
      updateDateAndTimeSlot(firstDate, firstSlot);
    }
  }, [hasValidTimeSlots, hasAnySelection, timeSlots, updateDateAndTimeSlot]);

  useEffect(() => {
    if (!hasValidTimeSlots || !hasAnySelection) return;

    if (isCurrentSelectionValid) return;

    const firstSlot = getFirstSlot(timeSlots);
    if (firstSlot) {
      const firstDate = new Date(firstSlot.startTime);
      updateDateAndTimeSlot(firstDate, firstSlot);
    }
  }, [
    hasValidTimeSlots,
    hasAnySelection,
    isCurrentSelectionValid,
    timeSlots,
    updateDateAndTimeSlot,
  ]);

  const handleDateChange = useCallback(
    (date: Date) => {
      const firstSlotForDate = findSlotForDate(timeSlots, date);
      updateDateAndTimeSlot(date, firstSlotForDate);
    },
    [timeSlots, updateDateAndTimeSlot],
  );

  const handleTimeSlotChange = useCallback(
    (timeSlot: TimeSlot | null) => {
      updateTimeSlot(timeSlot);
    },
    [updateTimeSlot],
  );

  return {
    selectedDate,
    selectedStartTime: selectedStartTime ?? '',
    selectedEndTime: selectedEndTime ?? '',
    handleDateChange,
    handleTimeSlotChange,
  };
};

export default useTimeSlotSelection;
